import { useFlags } from '@atlaskit/flag';
import { VehiclesApiService } from 'core/api';
import { ILocationDao } from 'core/api/types/locations.interface';
import { useDialog } from 'core/providers/DialogProvider';
import { PageElement } from 'core/utilities/interface-helpers';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { useNavigate } from 'react-router-dom';
import { IVehicleDao } from 'core/api/types/vehicles.interface';
import { useCollection } from 'core/hooks/useCollection';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import AddEditVehicleDialog from './dialogs/add-edit-vehicle-dialog';
import { FuelTypes, getFuelTypeNameFromKey } from 'core/constants/fuel-types';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Checkbox from '@atlaskit/checkbox';
import { QueryConstraint, where } from 'firebase/firestore';
import ExportDialog from 'modules/dialogs/export-dialog';
import { IButton } from 'shared/components/buttons/button-interface';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';

const VehiclesTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Registration', key: 'registration', templateId: 'text', width: 10 },
  { label: 'Make', key: 'make', templateId: 'text', width: 15 },
  { label: 'Model', key: 'model', templateId: 'text', width: 15 },
  { label: 'Fuel type', key: 'fuelType', templateId: 'fuelType', width: 10 },
  { label: 'Status', key: 'active', templateId: 'boolean', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const VehiclesList = () => {
  const dialog = useDialog();
  const flags = useFlags();
  const navigate = useNavigate();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<IVehicleDao>({
    apiService: VehiclesApiService,
    constraints,
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '')
        .map((filter) => {
          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (vehicle: IVehicleDao) => {
    return (
      <Checkbox
        isChecked={selectedRows.includes(vehicle.uid)}
        onChange={(e: ChangeEvent<HTMLInputElement>) => rowChecked(e, vehicle.uid)}
      />
    );
  };

  const actionCellTemplate = (vehicle: IVehicleDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => navigate(vehicle.uid)}
        type='button'
        spacing='compact'
        appearance='subtle'
        label='View'
      />
      <SharedButton
        onClick={() => dialog?.openDialog(<AddEditVehicleDialog vehicle={vehicle} />)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Update'
      />
      <SharedButton
        onClick={() =>
          dialog?.openDialog(
            <ConfirmationDialog
              title='Delete vehicle'
              textContent='Are you sure you want to delete this vehicle? This action cannot be undone.'
              action={() => deleteItem(VehiclesApiService, vehicle, flags, dialog)}
              buttonLabel='Delete Vehicle'
            />
          )
        }
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Delete'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'action',
    },
    {
      template: (location: ILocationDao) => <p>{location.address.fullAddress.postcode}</p>,
      id: 'postcode',
    },
    {
      template: (vehicle: IVehicleDao) => <p>{getFuelTypeNameFromKey(vehicle.fuelType)}</p>,
      id: 'fuelType',
    },
    { template: selectCellTemplate, id: 'select' },
  ];

  const headerActions: PageElement[] = [
    {
      key: 'exportVehicles',
      element: (
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ExportDialog
                title='Export Vehicles'
                apiService={VehiclesApiService}
                functionName={FirebaseFunctionNames.EXPORT_VEHICLES}
                filterQuery={filterQuery}
              />
            )
          }
          type='button'
          appearance='default'
          label='Export Vehicles'
        />
      ),
    },
    ...(selectedRows.length
      ? [
          {
            key: 'deleteVehicles',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete vehicles'
                      textContent='Are you sure you want to delete these vehicles? This action cannot be undone.'
                      action={() => deleteItems(VehiclesApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Vehicles'
                    />
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Vehicles'
              />
            ),
          },
        ]
      : [
          {
            key: 'addVehicle',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<AddEditVehicleDialog />)}
                type='button'
                appearance='primary'
                label='Add Vehicle'
              />
            ),
          },
        ]),
  ];

  const filters: Filter[] = [
    { id: 'registration', label: 'Registration' },
    { id: 'make', label: 'Make' },
    { id: 'model', label: 'Model' },
    {
      id: 'fuelType',
      label: 'Fuel Type',
      options: FuelTypes,
    },
    {
      id: 'active',
      label: 'Status',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Vehicles' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={VehiclesTableColumns}
        customTemplates={customTemplates}
        rows={tableRows}
        isLoading={loading}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default VehiclesList;
